/**
 * KpiCard — a single summary metric. The chip on the right shows the
 * percentage change vs. the previous comparable window when the backend
 * supplies it.
 *
 * Tone:
 *   - positive trend → emerald
 *   - negative trend → rose
 *   - null trend (no prior data) → muted "—"
 */

import * as React from 'react'
import { cn } from '@/src/infrastructure/lib/utils'
import { motion } from 'motion/react'

interface Props {
  label: string
  value: string
  icon?: React.ReactNode
  changePct?: number | null
  /** Hide the trend chip entirely (e.g. for "lifetime totals"). */
  hideTrend?: boolean
  /** Optional caption rendered under the value (e.g. "this month"). */
  caption?: string
}

export function KpiCard({ label, value, icon, changePct, hideTrend, caption }: Props) {
  const trend = formatTrend(changePct)

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-surface rounded-2xl border border-outline-variant/30 p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
    >
      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
            {label}
          </span>
          <span className="text-2xl md:text-3xl font-bold text-on-surface leading-none">
            {value}
          </span>
        </div>
        {icon && (
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs">
        {caption ? (
          <span className="text-on-surface-variant">{caption}</span>
        ) : <span />}
        {!hideTrend && (
          <span className={cn('font-semibold px-2 py-0.5 rounded-full', trend.bg, trend.text)}>
            {trend.label}
          </span>
        )}
      </div>
    </motion.div>
  )
}

function formatTrend(changePct: number | null | undefined): {
  label: string
  bg: string
  text: string
} {
  if (changePct == null) {
    return {
      label: '— vs prior',
      bg: 'bg-on-surface-variant/10',
      text: 'text-on-surface-variant',
    }
  }
  const sign = changePct > 0 ? '+' : ''
  if (changePct === 0) {
    return {
      label: '0% vs prior',
      bg: 'bg-on-surface-variant/10',
      text: 'text-on-surface-variant',
    }
  }
  if (changePct > 0) {
    return {
      label: `${sign}${changePct}% vs prior`,
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-600',
    }
  }
  return {
    label: `${changePct}% vs prior`,
    bg: 'bg-rose-500/10',
    text: 'text-rose-600',
  }
}
